/** YouTube video IDs are 11 characters (base64url-ish). */
const YT_ID_RE = /^[a-zA-Z0-9_-]{11}$/;

export function isValidYoutubeVideoId(id) {
  return Boolean(id && YT_ID_RE.test(id));
}

/**
 * Extract a YouTube video / live broadcast ID from common share URLs.
 */
export function extractYoutubeVideoId(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  try {
    const u = new URL(trimmed);
    const host = u.hostname.replace(/^www\./, "").toLowerCase();

    if (host === "youtu.be") {
      const id = u.pathname.slice(1).split("/")[0]?.split("?")[0];
      return isValidYoutubeVideoId(id) ? id : null;
    }

    if (host === "studio.youtube.com") {
      const match = u.pathname.match(/\/video\/([a-zA-Z0-9_-]{11})/);
      return match?.[1] && isValidYoutubeVideoId(match[1]) ? match[1] : null;
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      const fromQuery = u.searchParams.get("v");
      if (isValidYoutubeVideoId(fromQuery)) return fromQuery;

      const pathMatch = u.pathname.match(
        /(?:\/embed\/|\/live\/|\/shorts\/|\/v\/)([a-zA-Z0-9_-]{11})/
      );
      if (pathMatch?.[1]) return pathMatch[1];
    }
  } catch {
    // fall through to regex
  }

  const regexMatch = trimmed.match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|live|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return regexMatch?.[1] && isValidYoutubeVideoId(regexMatch[1])
    ? regexMatch[1]
    : null;
}

/**
 * Build an embed URL tuned for Safari (origin, mute+autoplay, playsinline).
 * @param {string} videoId
 * @param {{ origin?: string }} [opts]
 */
export function buildYoutubeEmbedUrl(videoId, opts = {}) {
  if (!isValidYoutubeVideoId(videoId)) return null;

  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    playsinline: "1",
    rel: "0",
    modestbranding: "1",
    controls: "1",
  });

  const origin = opts.origin?.trim();
  if (origin) {
    params.set("origin", origin);
  }

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

export function youtubeEmbedUrl(url, opts = {}) {
  const videoId = extractYoutubeVideoId(url);
  if (!videoId) return null;
  return buildYoutubeEmbedUrl(videoId, opts);
}
